// ── resources-collection: useCollection client adapter for /resources ─────────

import { useMemo } from "react";
import {
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import type { CoachingResourceDto } from "../../services/coaching-dtos";
import { readDirectoryInitialState } from "../directory-collection-state";
import { buildResourcesCollectionColumns as buildResourcesColumns, type ResourceRow } from "./resources-columns";
import {
  RESOURCE_CATEGORIES,
  resourceCategoryLabels,
  resourceCategoryOf,
  type ResourceCategory
} from "./resources-helpers";

export const RESOURCES_PAGE_SIZE = 25;

export function buildResourcesCollectionColumns(): CollectionColumnDef<ResourceRow>[] {
  return buildResourcesColumns() as CollectionColumnDef<ResourceRow>[];
}

export function categoryOptionsFromResources(rows: CoachingResourceDto[]): ResourceCategory[] {
  const seen = new Set<ResourceCategory>();
  for (const row of rows) seen.add(resourceCategoryOf(row));
  return RESOURCE_CATEGORIES.filter((category) => seen.has(category));
}

export function buildResourcesFilterDefs(rows: CoachingResourceDto[]): CollectionFilterDef<ResourceRow>[] {
  const categoryOptions = categoryOptionsFromResources(rows);
  const filters: CollectionFilterDef<ResourceRow>[] = [];

  if (categoryOptions.length > 1) {
    filters.push({
      id: "category",
      label: "Category",
      type: "select",
      placeholder: "All categories",
      getValue: (row) => resourceCategoryOf(row),
      options: categoryOptions.map((category) => ({ value: category, label: resourceCategoryLabels[category] }))
    });
  }

  filters.push({
    id: "scope",
    label: "Scope",
    type: "select",
    placeholder: "All scopes",
    getValue: (row) => (row.is_public ? "global" : "scoped"),
    options: [
      { value: "global", label: "Global" },
      { value: "scoped", label: "Scoped" }
    ]
  });

  return filters;
}

export function useResourcesCollection(
  rows: CoachingResourceDto[],
  routePath?: string
): UseCollectionResult<ResourceRow> {
  const filters = useMemo(() => buildResourcesFilterDefs(rows), [rows]);
  const columns = useMemo(() => buildResourcesCollectionColumns(), []);
  const initialState = useMemo(() => readDirectoryInitialState(routePath, RESOURCES_PAGE_SIZE), [routePath]);

  return useCollection<ResourceRow>({
    mode: "client",
    rows: rows as ResourceRow[],
    columns,
    filters,
    pageSize: RESOURCES_PAGE_SIZE,
    urlSync: "query",
    views: "coaching-resources",
    initialState,
    getRowKey: (row) => row.id
  });
}
